/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Download, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { createRegisterCodes } from '../api'
import { REGISTER_CODE_VALIDATION } from '../constants'

const createSchema = z.object({
  count: z
    .number()
    .int()
    .min(REGISTER_CODE_VALIDATION.COUNT_MIN)
    .max(REGISTER_CODE_VALIDATION.COUNT_MAX),
  expired_time: z.number().optional(),
})

type CreateFormValues = z.infer<typeof createSchema>

type RegisterCodesCreateButtonProps = {
  onSuccess: () => void
}

function downloadCodesAsTxt(codes: string[], count: number) {
  const now = new Date()
  const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
  const filename = `注册码_${dateStr}_${count}个.txt`
  const content = codes.join('\n')
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function RegisterCodesCreateButton({
  onSuccess,
}: RegisterCodesCreateButtonProps) {
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [generatedCodes, setGeneratedCodes] = useState<string[] | null>(null)
  const [generatedCount, setGeneratedCount] = useState(0)

  const form = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      count: 1,
      expired_time: undefined,
    },
  })

  const onSubmit = async (data: CreateFormValues) => {
    setIsSubmitting(true)
    try {
      const payload: { count: number; expired_time?: number } = {
        count: data.count,
      }
      if (data.expired_time && data.expired_time > 0) {
        payload.expired_time = data.expired_time
      }
      const result = await createRegisterCodes(payload)
      if (result.success) {
        const codes = result.data || []
        setGeneratedCodes(codes)
        setGeneratedCount(data.count)
        toast.success(`成功创建 ${data.count} 个注册码`)
        onSuccess()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDownload = () => {
    if (generatedCodes && generatedCodes.length > 0) {
      downloadCodesAsTxt(generatedCodes, generatedCount)
    }
  }

  const handleClose = () => {
    setOpen(false)
    setGeneratedCodes(null)
    form.reset()
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      handleClose()
    } else {
      setOpen(true)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger render={<Button size='sm' />}>
        <Plus className='h-4 w-4' />
        生成注册码
      </DialogTrigger>
      <DialogContent className='sm:max-w-[500px]'>
        {!generatedCodes ? (
          <>
            <DialogHeader>
              <DialogTitle>生成注册码</DialogTitle>
              <DialogDescription>
                批量生成注册码，用于用户注册。
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form
                id='register-code-form'
                onSubmit={form.handleSubmit(onSubmit)}
                className='space-y-4'
              >
                <FormField
                  control={form.control}
                  name='count'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>数量</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type='number'
                          min={REGISTER_CODE_VALIDATION.COUNT_MIN}
                          max={REGISTER_CODE_VALIDATION.COUNT_MAX}
                          placeholder='生成数量'
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value, 10) || 1)
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        一次最多生成 100 个注册码
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='expired_time'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>过期时间（可选）</FormLabel>
                      <FormControl>
                        <Input
                          value={field.value ?? ''}
                          type='number'
                          placeholder='Unix 时间戳，留空表示永不过期'
                          onChange={(e) => {
                            const val = e.target.value
                            field.onChange(val ? parseInt(val, 10) : undefined)
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        设置过期 Unix 时间戳，留空表示永不过期
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
            <DialogFooter>
              <Button variant='outline' onClick={handleClose}>
                取消
              </Button>
              <Button form='register-code-form' type='submit' disabled={isSubmitting}>
                {isSubmitting ? '生成中...' : '生成'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>生成成功</DialogTitle>
              <DialogDescription>
                共生成 {generatedCodes.length} 个注册码，可点击下方按钮下载 TXT 文件。
              </DialogDescription>
            </DialogHeader>
            <div className='max-h-[300px] overflow-y-auto rounded-md border p-3'>
              <Textarea
                readOnly
                value={generatedCodes.join('\n')}
                className='min-h-[200px] font-mono text-sm'
              />
            </div>
            <DialogFooter className='gap-2 sm:gap-0'>
              <Button variant='outline' onClick={handleClose}>
                关闭
              </Button>
              <Button onClick={handleDownload}>
                <Download className='mr-2 h-4 w-4' />
                下载 TXT
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
